// Message decoders (protocol §5). Every malformed message throws ProtocolError:
// - an unknown opcode;
// - a message shorter than its layout, or with bytes left over (except FRAME, whose sections run
//   to the end of the message);
// - a `str` over its byte limit, a varint over 5 bytes or 2³² − 1, a non-finite f32;
// - a count over its limit, an unknown entity kind, MOVE mask, mode or match state.
// The server closes with 4004 on any of these (protocol §9.2).

import {
  HELLO_MAGIC,
  MAX_CLIENT_MESSAGE_BYTES,
  MAX_MASS_SAMPLES,
  MAX_STANDINGS,
  STR_MAX_BYTES,
  WHEEL_ZOOM_WIRE_MAX,
  WHEEL_ZOOM_WIRE_MIN,
} from '../constants.js';
import { clamp } from '../math/clamp.js';
import { MODE_CODE, type ModeCode } from '../modes.js';
import { readFrameBody } from './frame.js';
import {
  ARENA_CAUSE_CODE,
  ARENA_LIFE_FLAG,
  ARENA_SHAPE_CODE,
  type ArenaCauseCode,
  C2S,
  LABEL_CURRENCY,
  OPTIONS_FLAG,
  PLAY_FLAG,
  S2C,
  STANDING_FLAG,
  WELCOME_FLAG,
  WELCOME_SIM,
} from './opcodes.js';
import { DEFAULT_QUANT, fractionFromWire, type Quant } from './quant.js';
import { ProtocolError, Reader } from './reader.js';
import type {
  ArenaLifeInfo,
  CashOutDoneMessage,
  ClientMessage,
  DeathMessage,
  LifeSummary,
  MatchResultMessage,
  ServerMessage,
  StandingEntry,
  WelcomeArenaInfo,
  WelcomeMessage,
  WelcomeMoneyInfo,
} from './types.js';
import { PROTOCOL_VERSION } from './version.js';

const MAX_MODE_CODE = MODE_CODE.rush;

function toBytes(data: Uint8Array | ArrayBufferLike): Uint8Array {
  return data instanceof Uint8Array ? data : new Uint8Array(data);
}

/** The opcode (first byte) of a message, or -1 for an empty message. */
export function peekOpcode(data: Uint8Array | ArrayBufferLike): number {
  const b = toBytes(data);
  return b.length > 0 ? (b[0] as number) : -1;
}

// ---------------------------------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------------------------------

/**
 * Decodes a client message (protocol §5.1). A HELLO whose protocolVersion is not ours is
 * returned without its remaining fields (`clientBuild: ""`), whatever follows the version, so the
 * server can answer ERROR 1 / close 4001 rather than 4004 (protocol §4).
 */
export function decodeClientMessage(data: Uint8Array | ArrayBufferLike): ClientMessage {
  const bytes = toBytes(data);
  if (bytes.length === 0) throw new ProtocolError('empty message');
  if (bytes.length > MAX_CLIENT_MESSAGE_BYTES) {
    throw new ProtocolError(
      `client message of ${bytes.length} bytes exceeds ${MAX_CLIENT_MESSAGE_BYTES}`,
    );
  }
  const r = new Reader(bytes);
  const op = r.u8();
  let msg: ClientMessage;
  switch (op) {
    case C2S.HELLO: {
      for (const expected of HELLO_MAGIC) {
        const at = r.offset;
        if (r.u8() !== expected) throw new ProtocolError('bad HELLO magic', at);
      }
      const protocolVersion = r.u16();
      if (protocolVersion !== PROTOCOL_VERSION) {
        return { op: C2S.HELLO, protocolVersion, clientBuild: '' };
      }
      const clientBuild = r.str(STR_MAX_BYTES.clientBuild);
      // Optional trailer: a signed-in player's authToken (absent = anonymous). An empty string on
      // the wire is malformed (an encoder never writes it), so "absent" has one spelling.
      if (r.remaining > 0) {
        const at = r.offset;
        const authToken = r.str(STR_MAX_BYTES.authToken);
        if (authToken === '') throw new ProtocolError('empty HELLO authToken', at);
        msg = { op: C2S.HELLO, protocolVersion, clientBuild, authToken };
      } else {
        msg = { op: C2S.HELLO, protocolVersion, clientBuild };
      }
      break;
    }
    case C2S.PLAY: {
      const nick = r.str(STR_MAX_BYTES.nick);
      const skinId = r.str(STR_MAX_BYTES.skinId);
      const raw = r.u8();
      const flags = raw & ~PLAY_FLAG.HAS_TICKET;
      msg =
        (raw & PLAY_FLAG.HAS_TICKET) !== 0
          ? { op: C2S.PLAY, nick, skinId, flags, ticket: r.str(STR_MAX_BYTES.ticketId) }
          : { op: C2S.PLAY, nick, skinId, flags };
      break;
    }
    case C2S.INPUT:
      msg = { op: C2S.INPUT, targetX: r.f32Finite(), targetY: r.f32Finite() };
      break;
    case C2S.SPLIT:
    case C2S.EJECT:
    case C2S.SPECIAL:
    case C2S.SPECTATE:
    case C2S.CASH_OUT_START:
    case C2S.CASH_OUT_CANCEL:
      msg = { op };
      break;
    case C2S.OPTIONS: {
      const flags = r.u8();
      const zClamped = clamp(r.u16(), WHEEL_ZOOM_WIRE_MIN, WHEEL_ZOOM_WIRE_MAX);
      msg = {
        op: C2S.OPTIONS,
        wantMinimapPlayers: (flags & OPTIONS_FLAG.WANT_MINIMAP_PLAYERS) !== 0,
        menuOpen: (flags & OPTIONS_FLAG.MENU_OPEN) !== 0,
        wheelZoom: zClamped / 100,
      };
      break;
    }
    case C2S.PING:
      msg = { op: C2S.PING, clientTimeMs: r.u32() };
      break;
    case C2S.ARENA_INPUT:
      msg = { op: C2S.ARENA_INPUT, heading: r.u16(), seq: r.u16() };
      break;
    default:
      throw new ProtocolError(`unknown client opcode 0x${op.toString(16).padStart(2, '0')}`, 0);
  }
  r.expectEnd();
  return msg;
}

// ---------------------------------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------------------------------

function readModeCode(r: Reader): ModeCode {
  const at = r.offset;
  const mode = r.u8();
  if (mode > MAX_MODE_CODE) throw new ProtocolError(`unknown mode code ${mode}`, at);
  return mode as ModeCode;
}

function readPositiveF32(r: Reader, what: string): number {
  const at = r.offset;
  const v = r.f32Finite();
  if (!(v > 0)) throw new ProtocolError(`${what} must be > 0, got ${v}`, at);
  return v;
}

function readWelcome(r: Reader): WelcomeMessage {
  const protocolVersion = r.u16();
  const tickRate = readPositiveF32(r, 'tickRate');
  const serverBuild = r.str(STR_MAX_BYTES.serverBuild);
  const roomId = r.str(STR_MAX_BYTES.roomId);
  const mode = readModeCode(r);
  const minX = r.f32Finite();
  const minY = r.f32Finite();
  const maxX = r.f32Finite();
  const maxY = r.f32Finite();
  const posQ = readPositiveF32(r, 'posQ');
  const radQ = readPositiveF32(r, 'radQ');
  const maxCells = r.u8();
  const teamCount = r.u8();
  const teamColors: number[] = [];
  for (let i = 0; i < teamCount; i++) teamColors.push(r.rgb());
  const connectionId = r.vu();
  const flags = r.u8();
  const welcome: WelcomeMessage = {
    op: S2C.WELCOME,
    protocolVersion,
    tickRate,
    serverBuild,
    roomId,
    mode,
    minX,
    minY,
    maxX,
    maxY,
    posQ,
    radQ,
    maxCells,
    teamColors,
    connectionId,
    customSkinsEnabled: (flags & WELCOME_FLAG.CUSTOM_SKINS_ENABLED) !== 0,
    minimapPlayersAllowed: (flags & WELCOME_FLAG.MINIMAP_PLAYERS_ALLOWED) !== 0,
    spectateAllowed: (flags & WELCOME_FLAG.SPECTATE_ALLOWED) !== 0,
    customSkinsPublic: (flags & WELCOME_FLAG.CUSTOM_SKINS_PUBLIC) !== 0,
    partyCode: r.str(STR_MAX_BYTES.partyCode),
    leaderboardEveryTicks: r.u16(),
    minimapEveryTicks: r.u16(),
    virusMass: r.f32Finite(),
    ejectPelletMass: r.f32Finite(),
    virusFeedsToShoot: r.u8(),
  };
  if ((flags & WELCOME_FLAG.MONEY_ROOM) !== 0) {
    welcome.money = readWelcomeMoney(r);
    if ((flags & WELCOME_FLAG.PRACTICE_ROOM) !== 0) welcome.money.practice = true;
  }
  // The `u8 sim` trailer (protocol §5.3): absent = the cell World.
  if (r.remaining > 0) {
    const at = r.offset;
    const sim = r.u8();
    if (sim === WELCOME_SIM.TERRITORY) welcome.arena = readWelcomeArena(r);
    else if (sim !== WELCOME_SIM.CELLS) throw new ProtocolError(`unknown WELCOME sim ${sim}`, at);
  }
  return welcome;
}

function readWelcomeArena(r: Reader): WelcomeArenaInfo {
  const cols = r.u16();
  const rows = r.u16();
  const at = r.offset;
  const shape = r.u8();
  if (shape !== ARENA_SHAPE_CODE.CIRCLE && shape !== ARENA_SHAPE_CODE.SQUARE) {
    throw new ProtocolError(`unknown arena shape ${shape}`, at);
  }
  return {
    shape: shape === ARENA_SHAPE_CODE.CIRCLE ? 'circle' : 'square',
    cols,
    rows,
    playableCells: r.u32(),
    speed: readPositiveF32(r, 'arena speed'),
    turnRateDeg: readPositiveF32(r, 'arena turnRateDeg'),
    cellsDiagonal: readPositiveF32(r, 'arena cellsDiagonal'),
    zoomOutMax: readPositiveF32(r, 'arena zoomOutMax'),
    shieldSec: r.f32Finite(),
  };
}

const MAX_CAUSE_CODE = ARENA_CAUSE_CODE.EXIT_CAPTURED;

/** The arena trailer of DEATH and CASH_OUT_DONE (protocol §5.2). */
function readArenaLife(r: Reader): ArenaLifeInfo {
  const at = r.offset;
  const cause = r.u8();
  if (cause > MAX_CAUSE_CODE) throw new ProtocolError(`unknown arena death cause ${cause}`, at);
  const flags = r.u8();
  return {
    cause: cause as ArenaCauseCode,
    atWall: (flags & ARENA_LIFE_FLAG.AT_WALL) !== 0,
    forcedExit: (flags & ARENA_LIFE_FLAG.FORCED_EXIT) !== 0,
    killerPlayerId: r.vu(),
    killerName: r.str(STR_MAX_BYTES.playerName),
    landPct: r.u16() / 100,
    maxLandPct: r.u16() / 100,
    kills: r.vu(),
    captures: r.vu(),
  };
}

function readWelcomeMoney(r: Reader): WelcomeMoneyInfo {
  const stakeUsd = readPositiveF32(r, 'stakeUsd');
  const rake = fractionFromWire(r.u16());
  const solUsd = r.f32Finite();
  const minLifeSec = r.f32Finite();
  const cashOutHoldSec = r.f32Finite();
  const at = r.offset;
  const currency = r.u8();
  if (currency !== LABEL_CURRENCY.USD && currency !== LABEL_CURRENCY.SOL) {
    throw new ProtocolError(`unknown label currency ${currency}`, at);
  }
  return {
    stakeUsd,
    rake,
    solUsd,
    minLifeSec,
    cashOutHoldSec,
    labelCurrency: currency === LABEL_CURRENCY.SOL ? 'SOL' : 'USD',
    cashOutKey: r.str(STR_MAX_BYTES.cashOutKey),
  };
}

function readLifeSummary(r: Reader, what: string): LifeSummary {
  const pelletsEaten = r.u32();
  const highestMass = r.u32();
  const timeAliveSec = r.u32();
  const boardTimeSec = r.u32();
  const ballsEaten = r.u32();
  const bestRank = r.u16();
  const sampleIntervalSec = r.u16();
  const at = r.offset;
  const n = r.vu();
  if (n > MAX_MASS_SAMPLES)
    throw new ProtocolError(`${what} sampleCount ${n} > ${MAX_MASS_SAMPLES}`, at);
  const samples: number[] = [];
  for (let i = 0; i < n; i++) samples.push(r.vu());
  return {
    pelletsEaten,
    highestMass,
    timeAliveSec,
    boardTimeSec,
    ballsEaten,
    bestRank,
    sampleIntervalSec,
    samples,
  };
}

function readDeath(r: Reader): DeathMessage {
  const death: DeathMessage = { op: S2C.DEATH, ...readLifeSummary(r, 'DEATH') };
  // Money trailer (money rooms only).
  if (r.remaining > 0) death.valueLost = r.vl();
  // Arena trailer (arena rooms): after valueLost.
  if (r.remaining > 0) death.arena = readArenaLife(r);
  return death;
}

function readCashOutDone(r: Reader): CashOutDoneMessage {
  const value = r.vl();
  const solUsd = r.f32Finite();
  const done: CashOutDoneMessage = {
    op: S2C.CASH_OUT_DONE,
    value,
    solUsd,
    stats: readLifeSummary(r, 'CASH_OUT_DONE'),
  };
  if (r.remaining > 0) done.arena = readArenaLife(r);
  return done;
}

function readMatchResult(r: Reader): MatchResultMessage {
  const at = r.offset;
  const mode = r.u8();
  if (mode === MODE_CODE.battleroyale) {
    const placement = r.u16();
    const totalPlayers = r.u16();
    const highestMass = r.u32();
    const ballsEaten = r.u32();
    const canSpectate = r.u8() !== 0;
    const countAt = r.offset;
    const n = r.u8();
    if (n > MAX_STANDINGS)
      throw new ProtocolError(`MATCH_RESULT standings ${n} > ${MAX_STANDINGS}`, countAt);
    const standings: StandingEntry[] = [];
    for (let i = 0; i < n; i++) {
      const name = r.str(STR_MAX_BYTES.standingName);
      standings.push({ name, isYou: (r.u8() & STANDING_FLAG.IS_YOU) !== 0 });
    }
    return {
      op: S2C.MATCH_RESULT,
      mode: MODE_CODE.battleroyale,
      placement,
      totalPlayers,
      highestMass,
      ballsEaten,
      canSpectate,
      standings,
    };
  }
  if (mode === MODE_CODE.teamrush) {
    const winnerTeam = r.u8();
    const n = r.u8();
    const shares: number[] = [];
    for (let i = 0; i < n; i++) shares.push(fractionFromWire(r.u16()));
    return {
      op: S2C.MATCH_RESULT,
      mode: MODE_CODE.teamrush,
      winnerTeam,
      shares,
      highestMass: r.u32(),
      ballsEaten: r.u32(),
    };
  }
  throw new ProtocolError(`MATCH_RESULT for mode ${mode} has no layout`, at);
}

/**
 * Decodes a server message (protocol §5.2). `quant` is the posQ/radQ pair from this connection's
 * WELCOME and is used to dequantise FRAME positions and radii.
 */
export function decodeServerMessage(
  data: Uint8Array | ArrayBufferLike,
  quant: Quant = DEFAULT_QUANT,
): ServerMessage {
  const bytes = toBytes(data);
  if (bytes.length === 0) throw new ProtocolError('empty message');
  const r = new Reader(bytes);
  const op = r.u8();
  let msg: ServerMessage;
  switch (op) {
    case S2C.WELCOME:
      msg = readWelcome(r);
      break;
    case S2C.FRAME:
      return readFrameBody(r, quant);
    case S2C.SPAWNED:
      msg = { op: S2C.SPAWNED, playerId: r.vu() };
      break;
    case S2C.DEATH:
      msg = readDeath(r);
      break;
    case S2C.PONG:
      msg = { op: S2C.PONG, clientTimeMs: r.u32() };
      break;
    case S2C.NOTICE:
      msg = { op: S2C.NOTICE, code: r.u16(), arg: r.str(STR_MAX_BYTES.noticeArg) };
      break;
    case S2C.PARTY:
      msg = { op: S2C.PARTY, code: r.str(STR_MAX_BYTES.partyCode), humanMembers: r.u8() };
      break;
    case S2C.MATCH_RESULT:
      msg = readMatchResult(r);
      break;
    case S2C.CASH_OUT_STATE:
      msg = {
        op: S2C.CASH_OUT_STATE,
        playerId: r.vu(),
        active: r.u8() !== 0,
        progress: fractionFromWire(r.u16()),
      };
      break;
    case S2C.CASH_OUT_DONE:
      msg = readCashOutDone(r);
      break;
    case S2C.ERROR:
      msg = { op: S2C.ERROR, code: r.u16(), message: r.str(STR_MAX_BYTES.errorMessage) };
      break;
    default:
      throw new ProtocolError(`unknown server opcode 0x${op.toString(16).padStart(2, '0')}`, 0);
  }
  r.expectEnd();
  return msg;
}

/** The posQ/radQ pair a WELCOME announces, for `decodeServerMessage`. */
export function quantOf(welcome: Pick<WelcomeMessage, 'posQ' | 'radQ'>): Quant {
  return { posQ: welcome.posQ, radQ: welcome.radQ };
}
